import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import zh-CN translation files
import zhCNCommon from '../locales/zh-CN/common.json';
import zhCNAuth from '../locales/zh-CN/auth.json';
import zhCNMenu from '../locales/zh-CN/menu.json';
import zhCNValidation from '../locales/zh-CN/validation.json';
import zhCNErrors from '../locales/zh-CN/errors.json';
import zhCNQuestionnaire from '../locales/zh-CN/questionnaire.json';
import zhCNProject from '../locales/zh-CN/project.json';
import zhCNWorkflow from '../locales/zh-CN/workflow.json';
import zhCNDocument from '../locales/zh-CN/document.json';
import zhCNUser from '../locales/zh-CN/user.json';
import zhCNScreening from '../locales/zh-CN/screening.json';
import zhCNChannel from '../locales/zh-CN/channel.json';
import zhCNAssessment from '../locales/zh-CN/assessment.json';
import zhCNSystemSettings from '../locales/zh-CN/systemSettings.json';

// Import en translation files
import enCommon from '../locales/en/common.json';
import enAuth from '../locales/en/auth.json';
import enMenu from '../locales/en/menu.json';
import enValidation from '../locales/en/validation.json';
import enErrors from '../locales/en/errors.json';
import enQuestionnaire from '../locales/en/questionnaire.json';
import enProject from '../locales/en/project.json';
import enWorkflow from '../locales/en/workflow.json';
import enDocument from '../locales/en/document.json';
import enUser from '../locales/en/user.json';
import enScreening from '../locales/en/screening.json';
import enChannel from '../locales/en/channel.json';
import enAssessment from '../locales/en/assessment.json';
import enSystemSettings from '../locales/en/systemSettings.json';

// Define resources
const resources = {
  'zh-CN': {
    common: zhCNCommon,
    auth: zhCNAuth,
    menu: zhCNMenu,
    validation: zhCNValidation,
    errors: zhCNErrors,
    questionnaire: zhCNQuestionnaire,
    project: zhCNProject,
    workflow: zhCNWorkflow,
    document: zhCNDocument,
    user: zhCNUser,
    screening: zhCNScreening,
    channel: zhCNChannel,
    assessment: zhCNAssessment,
    systemSettings: zhCNSystemSettings,
  },
  en: {
    common: enCommon,
    auth: enAuth,
    menu: enMenu,
    validation: enValidation,
    errors: enErrors,
    questionnaire: enQuestionnaire,
    project: enProject,
    workflow: enWorkflow,
    document: enDocument,
    user: enUser,
    screening: enScreening,
    channel: enChannel,
    assessment: enAssessment,
    systemSettings: enSystemSettings,
  },
};

// Supported languages
export const supportedLanguages = [
  { code: 'zh-CN', name: '简体中文', flag: '🇨🇳' },
  { code: 'en', name: 'English', flag: '🇺🇸' },
  // Future languages (uncomment when translations are ready)
  // { code: 'zh-TW', name: '繁體中文', flag: '🇹🇼' },
  // { code: 'ja', name: '日本語', flag: '🇯🇵' },
  // { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
];

// Default namespace
export const defaultNS = 'common';

// All namespaces
export const namespaces = [
  'common',
  'auth',
  'menu',
  'validation',
  'errors',
  'questionnaire',
  'project',
  'workflow',
  'document',
  'user',
  'screening',
  'channel',
  'assessment',
  'systemSettings',
];

// Language storage key
const LANGUAGE_STORAGE_KEY = 'greenbond_language';

// Get saved language from localStorage
const getSavedLanguage = (): string | null => {
  try {
    return localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    return null;
  }
};

// Save language to localStorage
export const saveLanguage = (lang: string): void => {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  } catch {
    console.warn('Failed to save language preference');
  }
};

// Supported language codes
const supportedLngCodes = supportedLanguages.map(lang => lang.code);

// Convert detected language to supported language code
// e.g., 'zh' -> 'zh-CN', 'en-US' -> 'en'
const convertDetectedLanguage = (lng: string): string => {
  // Exact match
  if (supportedLngCodes.includes(lng)) {
    return lng;
  }

  // Handle Chinese variants
  if (lng.startsWith('zh')) {
    return 'zh-CN';
  }

  // Handle English variants
  if (lng.startsWith('en')) {
    return 'en';
  }

  // Default to English for unsupported languages
  return 'en';
};

// Initialize i18n
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    // English as fallback when browser language is not supported
    fallbackLng: 'en',
    // List of supported languages
    supportedLngs: supportedLngCodes,
    // Don't load language variants automatically
    nonExplicitSupportedLngs: false,
    // Load only the exact language code
    load: 'currentOnly',
    defaultNS,
    ns: namespaces,

    // Language detection options
    detection: {
      // Order of language detection methods:
      // 1. localStorage - user's saved preference
      // 2. navigator - browser language setting
      // 3. htmlTag - HTML lang attribute
      order: ['localStorage', 'navigator', 'htmlTag'],
      // Keys to use for detection and caching
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      // Cache user language in localStorage
      caches: ['localStorage'],
      // Convert detected language to supported format
      convertDetectedLanguage,
    },

    interpolation: {
      escapeValue: false, // React already escapes values
    },

    // React options - ensure components re-render on language change
    react: {
      useSuspense: false, // Disable suspense for initial load
      bindI18n: 'languageChanged loaded', // Re-render on language change
      bindI18nStore: 'added removed', // Re-render on store changes
    },

    // Debug mode (only in development)
    debug: process.env.NODE_ENV === 'development',
  });

// Listen for language changes and save to localStorage
i18n.on('languageChanged', lng => {
  // Normalize language code before saving
  const normalizedLng = convertDetectedLanguage(lng);
  if (normalizedLng !== lng) {
    // If the language was normalized, change to the normalized version
    i18n.changeLanguage(normalizedLng);
    return;
  }

  saveLanguage(lng);
  // Update HTML lang attribute
  document.documentElement.lang = lng;

  // Log for debugging
  if (process.env.NODE_ENV === 'development') {
    console.log(`[i18n] Language changed to: ${lng}`);
  }
});

// Set initial HTML lang attribute
const savedLang = getSavedLanguage();
if (savedLang) {
  document.documentElement.lang = savedLang;
}

export default i18n;
